/* eslint-disable @typescript-eslint/no-empty-function */
import FadeIn from 'legacy/common/components/transition/FadeIn';
import { useDeletePromoterOffer, useGetOffers } from 'legacy/events/hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import OfferCard from '../../offers/OfferCard';
import PromoterCard from '../../offers/PromoterCard';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import { OffersPageProps, OffersStep } from 'legacy/events/offers.types';
import { showError } from 'legacy/common/utils/toast';
import OfferListSkeleton from '../../offers/offer-list/OfferListSkeleton';
import OfferListEmptyState from '../../offers/offer-list/OfferListEmptyState';

const OffersList = ({
  eventId,
  setOffersStep,
  setOfferId
}: OffersPageProps) => {
  const { t } = useTranslation(['events']);
  
  const { data, refetch, isLoading, isFetching } = useGetOffers(eventId, {
    enabled: !!eventId
  });
  const offers = data?.offers;
  const contracts = offers?.flatMap((offer) => offer.contracts);

  const handleOfferClick = (offerId: string) => {
    setOfferId(offerId);
    setOffersStep(OffersStep.OFFER_DETAILS);
  };

  const deletePromoterOffer = useDeletePromoterOffer();

  const handleDeletePromoterOffer = (offerId: string, callback: () => void) => {
    deletePromoterOffer.mutate(
      {
        offerId
      },
      {
        onSuccess: () => {
          refetch();
          callback();
        },
        onError: (error) => {
          showError({
            title: t('settings.promoterOffers.page.errors.deleteOffer'),
            message: error?.message || t('settings.promoterOffers.page.errors.general')
          });
        }
      }
    );
  };

  const handleCreateOffer = () => {
    setOfferId(null);
    setOffersStep(OffersStep.CREATE_OFFER);
  };

  if (isLoading || isFetching) {
    return <OfferListSkeleton />;
  }

  if (data && data?.offers?.length === 0) {
    return <OfferListEmptyState handleCreateOffer={handleCreateOffer} />;
  }

  return (
    <>
      <FadeIn>
        <div className="pt-[36x] md:pt-[90px] -mt-[36px] pb-20 md:w-[630px] md:mx-auto md:px-0 px-4">
          <div className="my-4">
            <div className="font-bold text-base text-[#202020]">
              {t('settings.promoterOffers.page.availableOffers')}
            </div>
            {data?.offers?.map((offer) => (
              <div className="mt-4" key={offer.id}>
                <OfferCard
                  offer={offer}
                  handleDeletePromoterOffer={handleDeletePromoterOffer}
                  handleOfferClick={handleOfferClick}
                  setOffersStep={setOffersStep}
                  setOfferId={setOfferId}
                />
              </div>
            ))}
          </div>
          {contracts?.length > 0 && (
            <div className="my-4">
              <div className="font-bold text-base text-[#202020]">
                {t('settings.promoterOffers.page.promoters')} ({contracts?.length})
              </div>
              <div className="mt-4">
                {contracts?.map((contract) => (
                  <PromoterCard
                    key={contract.id}
                    contract={contract}
                    eventId={eventId}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </FadeIn>

      <div className="w-full bg-white fixed bottom-0 left-0 p-4 z-10 border-t pb-12 md:pb-4">
        <div className="w-full md:w-[343px] mx-auto">
          <div className="flex gap-4">
            <Button type={ButtonType.Primary} onClick={handleCreateOffer}>
              <div className="flex items-center justify-center">
                {t('settings.promoterOffers.page.createOfferButton')}
              </div>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OffersList;